import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { minutesToClock, type ClassOccurrence } from '@app/shared'
import clsx from 'clsx'

import {
  useCapturedLectures,
  useClassOccurrenceWeek,
  useClassOccurrences,
  useClock,
  useUploadQueue,
} from '../../lib/data'
import { nearestOccurrence, realStartMinutes } from '../../lib/class-labeling'
import {
  NO_NATIVE_SHELL,
  handOffNativeRecording,
  listNativePhone,
  listNativeRecordings,
  nativeRecorderAvailable,
  nativeRecorderStatus,
  nativeRecordingName,
  nativeUploaderOwns,
  readNativeRecording,
  scheduleNativeClasses,
  startNativeRecording,
  stopNativeRecording,
  type NativeRecorderStatus,
  type NativeRecordingEntry,
  type NativeSchedule,
  type NativeScheduleOccurrence,
  type NativeUploadTokenState,
} from '../../lib/native-recorder'
import { nativeUploadWords, usePhoneUploader } from '../../lib/native-upload'
import { NATIVE_STAGE_PREFIX, alarmRecordingsToImport, type QueueEntry } from '../../lib/upload-queue'
import {
  RECORDER_TEST_COURSE,
  RECORDER_TEST_TITLE,
  armingOccurrences,
  awaitingPhoneUpload,
  newRecorderTest,
  observeLectures,
  observeNativeUpload,
  observePrompts,
  observeQueue,
  observeRecordings,
  observeSchedule,
  observeStatus,
  planRecorderTest,
  readRecorderTest,
  recorderTestArming,
  recorderTestChecklist,
  uploadCourseFor,
  uploadTitleFor,
  wallDate,
  wallTime,
  writeRecorderTest,
  type RecorderTest,
  type RecorderTestPlan,
} from '../../lib/recorder-test'
import { useDismissable, useEntered } from '../../lib/popover'
import { clockLength } from '../../lib/time'
import { RecordingRow } from '../../components/recordings/RecordingRow'
import { LECTURE_STATUS_LABEL } from '../../components/school/derive'
import { UploadQueueSection, megabytes } from '../../routes/RecordLecture'
import { MBell, MCheck, MMic } from '../shell/icons'
import { BackRow, Band, BandHeading, Card, Eyebrow, ReadFailure, ScrollColumn, Skeleton } from '../shell/parts'

// ============================================================================
// 1l · RECORDINGS — the phone's own place to start one, watch it, and read the list back.
//
// THE OWNER'S WORDS, 2026-09-15: the School tab needs a place to START a recording manually,
// MANAGE recordings, and SEE HISTORY; a manual recording overrides any scheduled one; every
// recording is labelled with the nearest class occurrence; and the "NEU C61 starts in 10 min"
// notification dropped him on the upload page with no way back. This screen is all four answers.
//
// THREE SECTIONS, IN THE ORDER HE NEEDS THEM WALKING INTO A LECTURE HALL:
//   Record  — the button, with the class it will be filed under stated BEFORE it is pressed.
//   On its way — the durable upload queue, the SAME component `/record` renders (REC-1b, #99).
//              Not a copy of it: two lists of one queue is two accounts of one recording.
//   History — what the server has, newest first, in the server's own five words.
//
// WHAT THE BUTTON DOES IN A BROWSER TAB, WHICH IS WHERE IT LIVES TODAY. Nothing, and it says so:
// "Record on this phone needs the LifeOS app". A PWA cannot hold a microphone through a screen
// lock — that is the promise the mobile port already refused to print once (MISSION-CONTROL,
// PR #26: "records with the screen off" was rewritten because it is PWA-unkeepable). So the
// button is not a dead toggle; it is a sentence plus the path that does work today: share the
// file out of Samsung Voice Recorder, which lands on /record.
//
// WHAT IT DOES INSIDE THE NATIVE SHELL, which is not built yet and is fully wired anyway: start
// with the nearest occurrence's course prefilled, poll `status()` for a live elapsed time that is
// the SERVICE's clock rather than a timer running in this page, and on Stop hand the finished
// file to the upload queue. The one thing that does not work end to end is reading the file's
// bytes — see lib/native-recorder.ts → `readNativeRecording`, which names the exact contract the
// native lane has to satisfy. When it cannot, this screen says the recording is on the phone and
// where, and NOTHING is marked sent. A recording reported as uploaded when it was not is the one
// failure the whole REC-1 arc exists to prevent.
// ============================================================================

/** How often the live card asks the service where it stands. The service's clock, not ours. */
const STATUS_POLL_MS = 1_000

/**
 * ARM THE PHONE'S OWN ALARMS — the half of automatic recording this screen is responsible for.
 *
 * The owner asked for lectures to record themselves. Nothing in a web page can do that: at 10:10
 * on a Tuesday this app is not running. What it CAN do is hand the native shell the week's
 * timetable, once, while it is open — and the shell arms real Android alarms that fire with the
 * screen off and PROMPT at class time. One tap starts the recording: Android 14+ will not let an
 * alarm open the microphone by itself (docs/NATIVE-SPIKE.md).
 *
 * WALL-CLOCK, NOT INSTANTS. `ClassOccurrence` is a date plus minutes into the owner's day and
 * carries no timezone, deliberately. So the strings sent are wall-clock (`2026-09-15T10:00:00`)
 * and the plugin resolves them against `America/Los_Angeles`, applying Berkeley Time itself. The
 * daylight-saving arithmetic lives in ONE place, on the side that owns the alarm, with tests.
 *
 * RE-ARMED EVERY TIME THIS SCREEN OPENS, and idempotent by replacement on the native side. A
 * cancelled class disappears from the phone the next time the owner looks at this screen; it does
 * not linger because a merge kept it.
 */
function useNativeClassAlarms(
  week: ReturnType<typeof useClassOccurrenceWeek>,
  test: RecorderTest | null,
  nowMs: number,
): NativeAlarms {
  const [schedule, setSchedule] = useState<NativeSchedule | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [held, setHeld] = useState(false)
  /** Bumped to try again after a hold. */
  const [retry, setRetry] = useState(0)
  const armed = useRef<string | null>(null)

  // The week's real classes, plus the recorder self-test while it is armed — in ONE list, because
  // `schedule()` replaces everything it armed before (lib/recorder-test.ts, fact 1).
  const occurrences: NativeScheduleOccurrence[] = armingOccurrences(week.days, test, nowMs)
  // The key is what stops this re-arming on every render: same week, same alarms, no call.
  const key = JSON.stringify(occurrences)

  useEffect(() => {
    // A WEEK THAT DID NOT LOAD IS NOT AN EMPTY WEEK. Arming it would replace every alarm on the
    // phone with nothing; leaving the phone's last schedule alone is the safe answer.
    if (!nativeRecorderAvailable() || week.pending || week.error) return
    if (armed.current === key) return
    let live = true
    let timer: number | undefined
    void (async () => {
      // NEVER RE-ARM UNDER A RUNNING CLASS RECORDING. `replaceAll` cancels every alarm it set and
      // drops any meeting whose start has passed (AlarmMath.withinHorizon) — which, mid-lecture,
      // is THIS lecture, stop alarm included. Opening this screen during an alarm-started
      // recording would otherwise leave it running until someone pressed Stop. So: wait, and try
      // again once it has stopped. (The plugin keeps an in-progress meeting's stop alarm since
      // 2026-09-24; the hold stays for any phone still on an older build.)
      const status = await nativeRecorderStatus()
      if (!live) return
      if (status.available && status.value.recording && status.value.scheduled) {
        setHeld(true)
        timer = window.setTimeout(() => setRetry((n) => n + 1), ALARM_HOLD_RETRY_MS)
        return
      }
      setHeld(false)
      armed.current = key
      const answer = await scheduleNativeClasses(JSON.parse(key) as NativeScheduleOccurrence[])
      if (!live) return
      // A refusal is not shown as an error on the Record card: the shell may simply be a build
      // without the command. The recorder test DOES show it, because there it is the answer.
      setSchedule(answer.available ? answer.value : null)
      setRefused(answer.available ? null : answer.reason)
    })()
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [key, week.pending, week.error, retry])

  return { schedule, refused, held }
}

interface NativeAlarms {
  /** What the phone read back after the last arming. Null until it has answered. */
  schedule: NativeSchedule | null
  /** The plugin's own words when it would not arm. */
  refused: string | null
  /** True while arming waits for an alarm-started recording to finish. */
  held: boolean
}

/** How long to wait before asking again whether the class recording has stopped. */
const ALARM_HOLD_RETRY_MS = 30_000

/**
 * TELL THE PHONE WHAT THE SERVER ACKNOWLEDGED — and nothing more.
 *
 * The native file is the ONLY copy until the upload finishes, so the plugin will not delete it on
 * anybody's say-so except the server's. This passes along exactly that: the `clientUploadId` the
 * SERVER named, for a queue entry that came from the native recorder. The phone records it and
 * deletes the file 24 hours later — late enough that a bad receipt is still recoverable by hand.
 *
 * Once per path per session: `told` is what keeps a re-render from re-announcing a hand-off, and
 * re-announcing would be harmless anyway (the native side keeps the FIRST `handedOffAt`, so the
 * 24-hour clock cannot be restarted by chatter).
 */
function useNativeHandoff(entries: QueueEntry[]): void {
  const told = useRef(new Set<string>())

  useEffect(() => {
    if (!nativeRecorderAvailable()) return
    for (const entry of entries) {
      if (!entry.stageId?.startsWith(NATIVE_STAGE_PREFIX)) continue
      const receipt = entry.receiptClientUploadId
      if (!receipt) continue
      const path = entry.stageId.slice(NATIVE_STAGE_PREFIX.length)
      if (!path || told.current.has(path)) continue
      told.current.add(path)
      void handOffNativeRecording(path, receipt)
    }
  }, [entries])
}

export function MobileRecordings() {
  const [params] = useSearchParams()
  const { data: clock } = useClock()
  const today = clock?.date
  const nowMinutes = clock?.minutesIntoDay ?? 0

  const { data: occurrences, error: occurrencesError } = useClassOccurrences(today)
  const {
    data: lectures,
    isPending: lecturesPending,
    error: lecturesError,
    refetch: refetchLectures,
  } = useCapturedLectures(20)
  const queue = useUploadQueue(today)

  // AUTOMATIC RECORDING, in two lines. Only inside the native shell: a browser tab has no alarms
  // to arm, so it does not fetch the week either.
  const week = useClassOccurrenceWeek(today, { enabled: nativeRecorderAvailable() })
  const recorderTest = useRecorderTest()
  const alarms = useNativeClassAlarms(week, recorderTest.test, recorderTest.nowMs)
  const schedule = alarms.schedule
  useNativeHandoff(queue.entries)
  // THE PHONE'S OWN UPLOADER (2026-09-24). Its token state decides who sends each recording —
  // lib/native-recorder.ts → nativeUploaderOwns is the one statement of that rule.
  const phone = usePhoneUploader()
  const phoneToken: NativeUploadTokenState | null = phone.uploader.kind === 'on' ? phone.uploader.state : null
  const phoneKnown = phone.uploader.kind !== 'unknown'
  const phoneUploads = usePhoneUploads()
  const refusals = useAlarmRecordingImport(
    queue,
    recorderTest.test?.seen.stopped?.seenAt ?? null,
    phoneToken,
    phoneKnown,
  )
  useRecorderTestObservations(recorderTest, alarms.schedule, queue.entries, lectures, refetchLectures)

  // THE NOTIFICATION'S COURSE WINS OVER THE CLOCK'S GUESS. `/record?course=NEU%20C61` is what the
  // class_start push carries (apps/worker/src/lib/notifications.ts), and a course the owner was
  // just told about is knowledge, not a nearest-match.
  const asked = params.get('course')
  const nearest =
    today && occurrences
      ? nearestOccurrence(occurrences, { date: today, minutes: nowMinutes })
      : null
  const askedOccurrence = asked
    ? ((occurrences ?? []).find((o) => o.courseCode === asked) ?? null)
    : null
  const label: ClassOccurrence | null = askedOccurrence ?? nearest
  const courseCode = label?.courseCode ?? asked ?? null

  return (
    <>
      {/* THE WAY OUT. A pushed screen has no tab bar (V10 board 25), and this one is also where
          the class-time notification lands cold (`/record?course=…`), with nothing behind it —
          BackRow goes to School then (mobile/shell/nav.ts → backTarget). */}
      <Band band="school" className="px-[14px] pb-3 pt-[6px]">
        <BackRow label="School" />
        <BandHeading
          title="Recordings"
          subline={
            <span className="truncate">
              {courseCode ? `Next up: ${courseCode}` : 'No class near this time'}
            </span>
          }
        />
      </Band>

      <ScrollColumn>
        <RecordCard
          courseCode={courseCode}
          occurrence={label}
          askedByNotification={askedOccurrence !== null || (asked !== null && label === null)}
          queue={queue}
          schedule={schedule}
          phone={phone}
          phoneToken={phoneToken}
        />

        {nativeRecorderAvailable() && (
          <RecorderTestCard
            state={recorderTest}
            alarms={alarms}
            phoneUploads={phoneToken !== null}
            weekProblem={
              week.error
                ? 'This week’s classes could not be read, so the test cannot be armed: arming it would clear your real class alarms.'
                : week.pending
                  ? 'Reading this week’s classes first…'
                  : null
            }
            refusal={
              recorderTest.test?.seen.path
                ? (refusals.get(recorderTest.test.seen.path) ?? null)
                : null
            }
            lectureStatus={lectureStatusOf(recorderTest.test, lectures ?? [])}
          />
        )}

        {occurrencesError && <ReadFailure error={occurrencesError} what="Today’s classes" />}

        {/* THE SAME COMPONENT /record RENDERS. One queue, one list, one set of words. */}
        <UploadQueueSection queue={queue} lectures={lectures ?? []} />

        {/* What the PHONE is sending by itself. The web queue above never holds these. */}
        <PhoneUploadsSection recordings={phoneUploads} />

        <section className="flex flex-none flex-col gap-[9px]">
          <Eyebrow className="pl-[2px] text-microcopy">History</Eyebrow>
          {lecturesPending && <Skeleton height={56} className="rounded-row" />}
          {lecturesError && <ReadFailure error={lecturesError} what="Your recordings" />}
          {!lecturesPending && !lecturesError && (lectures ?? []).length === 0 && (
            <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
              Nothing has reached the server yet. A recording shows up here once it has been
              uploaded — before that it is in the list above, on this phone.
            </p>
          )}
          <ul className="flex flex-col gap-2">
            {(lectures ?? []).map((lecture) => (
              <li key={lecture.id}>
                <RecordingRow lecture={lecture} />
              </li>
            ))}
          </ul>
          <p className="text-[11px] leading-[1.45] text-microcopy text-pretty">
            These are the server’s own words for each recording: audio uploaded means the PC has
            not picked it up yet.
          </p>
        </section>
      </ScrollColumn>
    </>
  )
}

// ── the Record card ─────────────────────────────────────────────────────────

type Shell =
  /** Still asking the plugin. Nothing is offered while the answer is unknown. */
  | { kind: 'checking' }
  /** A browser tab, or a shell whose recorder command is not there. `reason` is its own words. */
  | { kind: 'absent'; reason: string }
  | { kind: 'present'; status: NativeRecorderStatus }

function RecordCard({
  courseCode,
  occurrence,
  askedByNotification,
  queue,
  schedule,
  phone,
  phoneToken,
}: {
  courseCode: string | null
  occurrence: ClassOccurrence | null
  askedByNotification: boolean
  queue: ReturnType<typeof useUploadQueue>
  schedule: NativeSchedule | null
  phone: ReturnType<typeof usePhoneUploader>
  phoneToken: NativeUploadTokenState | null
}) {
  const [shell, setShell] = useState<Shell>(() =>
    nativeRecorderAvailable() ? { kind: 'checking' } : { kind: 'absent', reason: NO_NATIVE_SHELL },
  )
  const [busy, setBusy] = useState(false)
  /** What the last action actually did. Null when there is nothing to report. */
  const [note, setNote] = useState<string | null>(null)
  /** Where the last finished recording was left when its bytes could not be read. */
  const [onDisk, setOnDisk] = useState<{ path: string; durationSec: number | null } | null>(null)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const probe = useCallback(async () => {
    const answer = await nativeRecorderStatus()
    if (!live.current) return
    setShell(answer.available ? { kind: 'present', status: answer.value } : { kind: 'absent', reason: answer.reason })
  }, [])

  // ONE probe on mount, then a poll only while something is actually recording. A screen that
  // polls an idle service every second is a screen spending battery to learn nothing.
  useEffect(() => {
    if (!nativeRecorderAvailable()) return
    void probe()
    // AND AGAIN WHEN THE PHONE IS UNLOCKED. A class alarm can start a recording while this screen
    // sits locked; a Record button still showing afterwards would be a stale read presented as now.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void probe()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [probe])

  const recording = shell.kind === 'present' && shell.status.recording
  useEffect(() => {
    if (!recording) return
    const timer = window.setInterval(() => void probe(), STATUS_POLL_MS)
    return () => window.clearInterval(timer)
  }, [probe, recording])

  const onStart = useCallback(async () => {
    setBusy(true)
    setNote(null)
    setOnDisk(null)
    // A MANUAL RECORDING OVERRIDES A SCHEDULED ONE (owner, 2026-09-15). The plugin holds exactly
    // one recorder, so starting here IS the override — there is no second stream to stop.
    const answer = await startNativeRecording(courseCode)
    if (!live.current) return
    if (answer.available) setShell({ kind: 'present', status: answer.value })
    else setNote(answer.reason)
    setBusy(false)
  }, [courseCode])

  const running = shell.kind === 'present' && shell.status.recording ? shell.status : null
  const onStop = useCallback(async () => {
    setBusy(true)
    setNote(null)
    // THE RECORDING KNOWS ITS OWN COURSE when an alarm started it — the class the alarm was for,
    // not whatever is nearest by the time Stop is pressed. A manual one was started with
    // `courseCode`, so the two agree. The recorder self-test goes up unfiled, named for what it is.
    const recordedCourse = running?.scheduled ? (running.courseCode ?? null) : courseCode
    const stopped = await stopNativeRecording()
    if (!live.current) return
    if (!stopped.available) {
      setNote(stopped.reason)
      setBusy(false)
      void probe()
      return
    }

    const { path, durationMs, bytes } = stopped.value
    const durationSec = Number.isFinite(durationMs) ? Math.round(durationMs / 1000) : null

    // THE PHONE'S OWN UPLOADER OWNS IT? Then this screen hands nothing to the web queue — two
    // senders would be two jobs for one lecture (lib/native-recorder.ts → nativeUploaderOwns).
    const upload = stopped.value.upload ?? null
    if (nativeUploaderOwns({ upload, startedAt: running?.startedAt ?? null }, phoneToken)) {
      setNote(
        upload
          ? `Saved on this phone — ${clockLength(durationSec ?? 0)}, ${megabytes(bytes)}. The phone ` +
              'uploads it on its own; LifeOS does not need to stay open.'
          : `Saved on this phone — ${clockLength(durationSec ?? 0)}, ${megabytes(bytes)} — but the ` +
              'phone did not hand it to its uploader (the recording may not have closed properly). ' +
              'Nothing has been sent.',
      )
      setOnDisk(upload ? null : { path, durationSec })
      setBusy(false)
      void probe()
      return
    }

    // The bytes, or the honest absence of them. Nothing here invents a blob.
    const read = await readNativeRecording(path)
    const outcome = await queue.importNative({
      path,
      courseCode: uploadCourseFor(recordedCourse),
      blob: read.available ? read.value : null,
      durationSec,
      title: uploadTitleFor(recordedCourse),
    })
    if (!live.current) return

    if (outcome.refused) {
      setNote(outcome.refused)
      setOnDisk({ path, durationSec })
    } else {
      setNote(
        `Saved on this phone — ${clockLength(durationSec ?? 0)}, ${megabytes(bytes)}. It is in the ` +
          'list below and will be sent while LifeOS is open.',
      )
      setOnDisk(null)
    }
    setBusy(false)
    void probe()
  }, [courseCode, phoneToken, probe, queue, running])

  return (
    <Card className="gap-[12px]">
      <div className="flex items-center gap-2">
        <MMic className="text-ink-2" />
        <span className="text-[12.5px] font-semibold leading-none text-ink-2">Record</span>
      </div>

      <ClassLine
        courseCode={courseCode}
        occurrence={occurrence}
        askedByNotification={askedByNotification}
      />

      {shell.kind === 'checking' && <Skeleton height={48} className="rounded-button" />}

      {shell.kind === 'absent' && (
        <>
          <p className="text-[13px] font-semibold leading-[1.3] text-ink">{shell.reason}</p>
          <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
            A web page cannot hold the microphone once the screen locks, so this browser will not
            pretend to. Record in Samsung Voice Recorder and share the file to LifeOS — it lands in
            the list below and goes up from there.
          </p>
          <Link
            to="/record?upload=1"
            className="mo-ink mo-press flex min-h-[44px] items-center justify-center rounded-button border border-line bg-surface px-4 text-[13px] font-medium leading-none text-ink-2"
          >
            Send a recording from this phone
          </Link>
        </>
      )}

      {shell.kind === 'present' && !shell.status.recording && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void onStart()}
          className="mo-press flex min-h-[56px] w-full items-center justify-center rounded-button bg-danger-solid px-4 text-[15px] font-semibold leading-none text-on-dark disabled:opacity-60"
        >
          {busy ? 'Starting…' : courseCode ? `Record ${courseCode}` : 'Record'}
        </button>
      )}

      {shell.kind === 'present' && shell.status.recording && (
        <LiveRecording status={shell.status} busy={busy} onStop={() => void onStop()} />
      )}

      {shell.kind === 'present' && <ScheduledLine schedule={schedule} />}

      {shell.kind === 'present' && <PhoneUploadLine phone={phone} />}

      {note && (
        <p
          role="status"
          className="rounded-row border border-line-2 bg-surface-2 px-3 py-[9px] text-[11.5px] leading-[1.45] text-ink-2 text-pretty"
        >
          {note}
        </p>
      )}

      {onDisk && (
        <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty">
          It is on this phone at <span className="break-all font-mono">{onDisk.path}</span>
          {onDisk.durationSec !== null && ` (${clockLength(onDisk.durationSec)})`}. Nothing has been
          sent and nothing has been deleted.
        </p>
      )}
    </Card>
  )
}

/**
 * WHAT THE PHONE WILL DO WITHOUT BEING ASKED — read back off the phone, never re-derived here.
 *
 * The one thing on this screen that is a promise about the future, so it is made only when the
 * shell has confirmed it: the time printed is the alarm the plugin actually armed, Berkeley Time
 * already inside it. Nothing is printed while the answer is unknown.
 */
function ScheduledLine({ schedule }: { schedule: NativeSchedule | null }) {
  if (!schedule) return null
  // The recorder self-test has its own card and its own words; this line is about classes. A
  // class already under way is still on the phone's list (its stop alarm is kept), but its prompt
  // is behind us — the line names the next one ahead.
  const nowMs = Date.now()
  const next = schedule.scheduled.find(
    (s) => s.courseCode !== RECORDER_TEST_COURSE && Date.parse(s.startsAt) > nowMs,
  )

  return (
    <div className="flex flex-col gap-[6px]">
      {next ? (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          The phone will prompt you for {next.courseCode} at{' '}
          {new Date(next.startsAt).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}{' '}
          · one tap starts the recording, it stops on its own at{' '}
          {new Date(next.endsAt).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}
          . Until you answer, it alerts again every 2 minutes until the class ends. The
          notification carries Stop and Not in class.
        </p>
      ) : (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          No class in the next seven days, so the phone will not prompt you.
        </p>
      )}
      {schedule.exactAlarms === false && (
        <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty">
          {schedule.reason ??
            'Android has not granted LifeOS exact alarms, so a class recording may start late.'}
        </p>
      )}
    </div>
  )
}

/**
 * THE PHONE UPLOADS ON ITS OWN — or it does not yet, and one tap changes that.
 *
 * Nothing is shown until the phone has answered, and nothing is offered by an app build that has
 * no uploader: "cannot" must not look like "off". The token is never on this screen; the phone
 * answers only whether it has one, under what label, and since when.
 */
function PhoneUploadLine({ phone }: { phone: ReturnType<typeof usePhoneUploader> }) {
  const { uploader, busy, problem, turnOn } = phone
  if (uploader.kind === 'unknown' || uploader.kind === 'unsupported') return null

  return (
    <div className="flex flex-col gap-[8px]">
      {uploader.kind === 'on' ? (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          This phone uploads recordings on its own after Stop, with LifeOS closed
          {uploader.state.label ? ` · “${uploader.state.label}”` : ''}
          {uploader.state.since ? ` since ${new Date(uploader.state.since).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
          .
        </p>
      ) : (
        <>
          <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
            Recordings upload only while LifeOS is open. One tap lets the phone send them by itself
            after Stop — it gets its own key, which you can revoke on the Connections screen.
          </p>
          <button type="button" disabled={busy} onClick={turnOn} className={clsx(SECONDARY_BUTTON, 'disabled:opacity-60')}>
            {busy ? 'Setting it up…' : 'Let the phone upload on its own'}
          </button>
        </>
      )}
      {problem && (
        <p
          role="status"
          className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty"
        >
          {problem}
        </p>
      )}
    </div>
  )
}

/** How often, while this screen is visible, the phone's own uploads are read back. */
const PHONE_UPLOADS_POLL_MS = 15_000

/**
 * The phone's own list, read while this screen is open, for the recordings its uploader owns.
 * The PHONE'S words, read back — nothing here decides whether one was uploaded.
 */
function usePhoneUploads(): NativeRecordingEntry[] {
  const [recordings, setRecordings] = useState<NativeRecordingEntry[]>([])

  useEffect(() => {
    if (!nativeRecorderAvailable()) return
    let live = true
    const read = async () => {
      if (document.visibilityState === 'hidden') return
      const answer = await listNativeRecordings()
      if (live && answer.available) setRecordings(answer.value.filter((r) => r.upload))
    }
    void read()
    const timer = window.setInterval(() => void read(), PHONE_UPLOADS_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void read()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return recordings
}

/** "On its way", for what the PHONE is sending. Same row shape as the web queue's, the phone's words. */
function PhoneUploadsSection({ recordings }: { recordings: NativeRecordingEntry[] }) {
  if (recordings.length === 0) return null
  const clock = (iso: string) => wallTime(Date.parse(iso))

  return (
    <section className="flex flex-col gap-[10px]">
      <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase leading-none tracking-[0.06em] text-muted">
        On its way — sent by the phone
      </h2>
      <ul className="flex flex-col gap-2">
        {recordings.map((recording) => {
          const words = recording.upload ? nativeUploadWords(recording.upload, clock) : null
          if (!words) return null
          const failed = recording.upload?.state === 'failed'
          return (
            <li key={recording.path}>
              <div className="flex flex-col gap-2 rounded-card border border-line bg-surface px-3 py-[10px]">
                <div className="flex min-h-[44px] w-full flex-col items-start gap-[3px] text-left">
                  <span className="break-all text-[13px] font-semibold leading-[1.3]">
                    {nativeRecordingName(recording.path)}
                  </span>
                  <span className="text-[11.5px] leading-[1.45] text-muted text-pretty">{words.line}</span>
                  <span className="text-[11.5px] leading-none text-microcopy">
                    {megabytes(recording.bytes)}
                    {' · '}
                    {/* What the SERVER was told: the recorder test goes up unfiled. */}
                    {uploadCourseFor(recording.courseCode) ?? 'No course'}
                    {' · still on this phone'}
                  </span>
                </div>
                {words.problem && (
                  <p
                    className={clsx(
                      'rounded-row border px-[10px] py-[7px] text-[11.5px] leading-[1.45] text-pretty',
                      failed
                        ? 'border-danger-line bg-danger-fill text-danger'
                        : 'border-warning-line bg-warning-fill text-warning',
                    )}
                  >
                    {words.problem}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
        The phone sends these by itself, with LifeOS closed, whenever it has a connection. An uploaded
        one stays on the phone for a day, then the phone removes its copy.
      </p>
    </section>
  )
}

/** The class this recording will be filed under, stated before anything is pressed. */
function ClassLine({
  courseCode,
  occurrence,
  askedByNotification,
}: {
  courseCode: string | null
  occurrence: ClassOccurrence | null
  askedByNotification: boolean
}) {
  if (!courseCode) {
    return (
      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
        No class is near this time, so a recording made now would be filed under no course. You can
        file it afterwards from the recording’s own page.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-[4px]">
      <p className="text-[13px] font-semibold leading-[1.3] text-ink">
        Will be filed under {courseCode}
      </p>
      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
        {occurrence ? (
          <>
            {occurrence.courseName} · starts {minutesToClock(realStartMinutes(occurrence))}
            {occurrence.location ? ` · ${occurrence.location}` : ''} · Berkeley Time, ten minutes
            after the {minutesToClock(occurrence.startMinutes)} on the calendar.
          </>
        ) : askedByNotification ? (
          // The notification named a course whose meeting is not in today's occurrences. That is
          // a real disagreement between two reads and it is shown rather than resolved silently.
          <>The notification named this course. Today’s schedule does not list a meeting for it.</>
        ) : (
          <>The nearest class to right now.</>
        )}
      </p>
    </div>
  )
}

/** The live card. Everything on it is read back from the service, including the elapsed time. */
function LiveRecording({
  status,
  busy,
  onStop,
}: {
  status: NativeRecorderStatus
  busy: boolean
  onStop: () => void
}) {
  const startedMs = status.startedAt ? Date.parse(status.startedAt) : NaN
  const elapsedSec = Number.isFinite(startedMs) ? Math.max(0, Math.round((Date.now() - startedMs) / 1000)) : null

  return (
    <div className="flex flex-col gap-[10px]">
      <div className="flex items-center gap-[9px] rounded-row border border-danger-line bg-danger-fill px-3 py-[10px]">
        <span className="size-[9px] flex-none animate-pulse rounded-full bg-danger-dot" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-[1.25] text-danger">Recording</p>
          <p className="mt-[4px] truncate text-[11px] leading-none text-danger-2">
            {/* ABSENT RATHER THAN ZERO: a service that did not report a start time has not told us
                how long it has been going, and '0:00' would be an answer we do not have. */}
            {elapsedSec === null ? 'started — length not reported' : `${clockLength(elapsedSec)} so far`}
            {status.path ? ` · ${status.path.split(/[\\/]/).pop()}` : ''}
          </p>
        </div>
      </div>

      {status.audioFocusLostAt && (
        <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty">
          Android silenced the microphone at {status.audioFocusLostAt}. Recording did not stop, but
          what was captured from then on may be silence.
        </p>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={onStop}
        className="mo-press flex min-h-[56px] w-full items-center justify-center rounded-button border border-line bg-surface px-4 text-[15px] font-semibold leading-none text-ink disabled:opacity-60"
      >
        {busy ? 'Stopping…' : 'Stop'}
      </button>
    </div>
  )
}

// ── alarm recordings, picked up ─────────────────────────────────────────────

/** How often, while this screen is visible, the phone's own list is checked for finished ones. */
const SWEEP_INTERVAL_MS = 60_000

/**
 * PUT WHAT THE ALARMS RECORDED INTO THE QUEUE — the step between "stopped by itself" and "uploads".
 *
 * A recording an alarm stopped, with the screen off and nobody pressing anything, was handed to
 * nobody: the Stop button's handler above is the only other door into the queue. So while this
 * screen is open it reads the phone's own list and imports every FINISHED, ALARM-STARTED recording
 * the queue does not already hold (`alarmRecordingsToImport` says exactly which). Same queue, same
 * receipt rule, same hand-off as a manual recording — there is still no second upload path.
 *
 * It answers the refusals, per file path, in the queue's own words: a recording that is on the
 * phone and could not be queued is a fact the recorder test must print, not swallow.
 *
 * IT SKIPS WHAT THE PHONE'S OWN UPLOADER OWNS (`alarmRecordingsToImport` applies
 * `nativeUploaderOwns`), and it does not run at all until the phone has said whether it holds an
 * upload token (`phoneKnown`) — sweeping on a guess is how one lecture becomes two jobs.
 */
function useAlarmRecordingImport(
  queue: ReturnType<typeof useUploadQueue>,
  nudge: string | null,
  phoneToken: NativeUploadTokenState | null,
  phoneKnown: boolean,
): Map<string, string> {
  const [refusals, setRefusals] = useState<Map<string, string>>(() => new Map())
  /** Once per path per visit: a refusal is not retried every minute, only when the screen reopens. */
  const tried = useRef(new Set<string>())
  const busy = useRef(false)
  const entries = useRef(queue.entries)
  entries.current = queue.entries
  const { importNative, ready } = queue

  const sweep = useCallback(async () => {
    // Not before the queue is open: demo mode never opens it, and a signed-in phone opens it in
    // a beat. Importing into a closed queue would only produce a refusal to show.
    if (!nativeRecorderAvailable() || !ready || !phoneKnown || busy.current) return
    busy.current = true
    try {
      const list = await listNativeRecordings()
      if (!list.available) return
      for (const recording of alarmRecordingsToImport(list.value, entries.current, phoneToken)) {
        if (tried.current.has(recording.path)) continue
        tried.current.add(recording.path)
        const read = await readNativeRecording(recording.path)
        const startedMs = recording.startedAt ? Date.parse(recording.startedAt) : Number.NaN
        const outcome = await importNative({
          path: recording.path,
          courseCode: uploadCourseFor(recording.courseCode),
          title: uploadTitleFor(recording.courseCode),
          blob: read.available ? read.value : null,
          durationSec:
            typeof recording.durationMs === 'number' ? Math.round(recording.durationMs / 1000) : null,
          // The day it was RECORDED — a lecture picked up the morning after is still that day's.
          ...(Number.isFinite(startedMs) ? { date: wallDate(startedMs) } : {}),
        })
        if (outcome.refused) {
          const reason = read.available ? outcome.refused : read.reason
          setRefusals((previous) => new Map(previous).set(recording.path, reason))
        }
      }
    } finally {
      busy.current = false
    }
  }, [importNative, ready, phoneKnown, phoneToken])

  useEffect(() => {
    void sweep()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sweep()
    }
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void sweep()
    }, SWEEP_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [sweep])

  // The recorder test just saw its file stop: look now rather than up to a minute from now.
  useEffect(() => {
    if (nudge) void sweep()
  }, [nudge, sweep])

  return refusals
}

// ── the recorder test ───────────────────────────────────────────────────────

/** How often the test card's clock moves while a test is on file. */
const TEST_CLOCK_MS = 5_000
/** How often the phone is asked about the test recording, until it has stopped. */
const TEST_PHONE_POLL_MS = 5_000
/** How often the server's lecture list is re-read while the test waits for its transcript. */
const TEST_LECTURE_POLL_MS = 30_000
/** With less lead than this left, the sheet plans a fresh two minutes rather than arm late. */
const TEST_LEAD_FLOOR_MS = 90_000

function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

interface RecorderTestState {
  test: RecorderTest | null
  /** The card's clock. Moves only while a test is on file. */
  nowMs: number
  arm: (plan: RecorderTestPlan) => void
  /** Cancel or dismiss: either way the test leaves the phone's alarms and this screen. */
  clear: () => void
  /** Apply an observation. Pure functions only — it can run twice under StrictMode. */
  update: (observe: (test: RecorderTest) => RecorderTest) => void
}

/** The armed test, kept in one localStorage key so a locked, reopened screen still has it. */
function useRecorderTest(): RecorderTestState {
  const [test, setTest] = useState<RecorderTest | null>(() =>
    readRecorderTest(browserStorage(), Date.now()),
  )
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    writeRecorderTest(browserStorage(), test)
  }, [test])

  const onFile = test !== null
  useEffect(() => {
    if (!onFile) return
    const tick = () => setNowMs(Date.now())
    tick()
    const timer = window.setInterval(tick, TEST_CLOCK_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [onFile])

  const arm = useCallback((plan: RecorderTestPlan) => {
    const now = Date.now()
    setNowMs(now)
    setTest(newRecorderTest(now, plan))
  }, [])
  const clear = useCallback(() => setTest(null), [])
  const update = useCallback((observe: (test: RecorderTest) => RecorderTest) => {
    setTest((previous) => (previous ? observe(previous) : previous))
  }, [])

  return { test, nowMs, arm, clear, update }
}

/**
 * FILL THE CHECKLIST FROM READS, AND ONLY FROM READS: the phone's read-back of its alarms, the
 * service's status and its list of files (polled until the file has stopped), the durable queue,
 * and the server's lecture list (re-read while a transcript is awaited).
 */
function useRecorderTestObservations(
  state: RecorderTestState,
  schedule: NativeSchedule | null,
  entries: QueueEntry[],
  lectures: { id: string; hasTranscript: boolean }[] | undefined,
  refetchLectures: () => unknown,
): void {
  const { test, update } = state
  const onFile = test !== null
  const path = test?.seen.path ?? null
  const lectureId = test?.seen.lectureId ?? null
  const stopped = Boolean(test?.seen.stopped)
  // After the stop, the phone is still the story while ITS uploader holds the file.
  const phonePending = awaitingPhoneUpload(test)
  const transcribed = Boolean(test?.seen.transcribed)

  useEffect(() => {
    if (onFile && schedule) update((t) => observeSchedule(t, schedule, Date.now()))
  }, [onFile, schedule, update])

  useEffect(() => {
    if (onFile && path) update((t) => observeQueue(t, entries))
  }, [onFile, path, entries, update])

  useEffect(() => {
    if (onFile && lectureId && lectures) update((t) => observeLectures(t, lectures, Date.now()))
  }, [onFile, lectureId, lectures, update])

  // The phone, until its file has stopped — and on, while the phone's own uploader holds it. After
  // that the queue (or the phone's receipt) and the server are the story.
  useEffect(() => {
    if (!onFile || (stopped && !phonePending) || !nativeRecorderAvailable()) return
    let live = true
    const poll = async () => {
      if (document.visibilityState === 'hidden') return
      const [status, phone] = await Promise.all([nativeRecorderStatus(), listNativePhone()])
      if (!live) return
      const now = Date.now()
      update((t) => {
        let next = t
        if (status.available) next = observeStatus(next, status.value, now)
        if (phone.available) {
          next = observePrompts(next, phone.value.prompts, now)
          next = observeRecordings(next, phone.value.recordings, now)
          next = observeNativeUpload(next, phone.value.recordings, now)
        }
        return next
      })
    }
    void poll()
    const timer = window.setInterval(() => void poll(), TEST_PHONE_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void poll()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [onFile, stopped, phonePending, update])

  // The server, while a lecture exists for the test and has no words yet.
  useEffect(() => {
    if (!onFile || !lectureId || transcribed) return
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refetchLectures()
    }, TEST_LECTURE_POLL_MS)
    return () => window.clearInterval(timer)
  }, [onFile, lectureId, transcribed, refetchLectures])
}

/** The server's own word for the test lecture while it has no transcript, or null. */
function lectureStatusOf(
  test: RecorderTest | null,
  lectures: { id: string; status: keyof typeof LECTURE_STATUS_LABEL }[],
): string | null {
  const id = test?.seen.lectureId
  if (!id || test?.seen.transcribed) return null
  const lecture = lectures.find((l) => l.id === id)
  return lecture ? LECTURE_STATUS_LABEL[lecture.status] : null
}

const SECONDARY_BUTTON =
  'mo-press flex min-h-[44px] w-full items-center justify-center rounded-button border border-line bg-surface px-4 text-[13px] font-medium leading-none text-ink-2'

function RecorderTestCard({
  state,
  alarms,
  phoneUploads,
  weekProblem,
  refusal,
  lectureStatus,
}: {
  state: RecorderTestState
  alarms: NativeAlarms
  /** True when the phone holds an upload token: the test goes up with LifeOS closed. */
  phoneUploads: boolean
  weekProblem: string | null
  refusal: string | null
  lectureStatus: string | null
}) {
  const [sheet, setSheet] = useState(false)
  const { test, nowMs } = state

  if (!test) {
    return (
      <Card className="gap-[10px]">
        <div className="flex items-center gap-2">
          <MBell size={16} strokeWidth={1.8} className="text-ink-2" />
          <span className="text-[12.5px] font-semibold leading-none text-ink-2">Recorder test</span>
        </div>
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          Run the whole class-time path at home — alarm, prompt, one tap, recording, upload,
          transcript — without waiting for a class.
        </p>
        <button type="button" onClick={() => setSheet(true)} className={SECONDARY_BUTTON}>
          Test the recorder
        </button>
        {sheet && (
          <RecorderTestSheet
            blocked={weekProblem}
            phoneUploads={phoneUploads}
            onClose={() => setSheet(false)}
            onArm={(plan) => {
              setSheet(false)
              state.arm(plan)
            }}
          />
        )}
      </Card>
    )
  }

  const promptMs = Date.parse(test.promptAt)
  const stopMs = Date.parse(test.stopAt)
  const arming = recorderTestArming(test, nowMs)
  const finished = !arming || Boolean(test.seen.transcribed)
  const rows = recorderTestChecklist(test, { refusal, lectureStatus })

  return (
    <Card className="gap-[12px]">
      <div className="flex items-center gap-2">
        <MBell size={16} strokeWidth={1.8} className="text-ink-2" />
        <span className="text-[12.5px] font-semibold leading-none text-ink-2">Recorder test</span>
        <span className="ml-auto text-[11px] leading-none text-microcopy">
          {finished ? 'Result' : nowMs < promptMs ? 'Armed' : 'Under way'}
        </span>
      </div>

      <p className="text-[13px] font-semibold leading-[1.35] text-ink text-pretty">
        Prompt due at {wallTime(promptMs)} · stops at {wallTime(stopMs)}
      </p>
      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
        {finished
          ? 'This list is the result: each line is only what this phone actually saw.'
          : nowMs < promptMs
            ? 'Lock the phone now and watch. It should prompt you; tap Start recording, and it stops by itself two minutes later.'
            : phoneUploads
              ? 'After it stops, the phone uploads it on its own — LifeOS does not need to be open. Open this screen again to read the result.'
              : 'After it stops, open this screen again — the upload only runs while LifeOS is open.'}{' '}
        This screen reads your answer off the phone, not how Android drew the prompt: note whether
        it filled the screen or came down as a banner.
      </p>

      {!test.seen.armed && weekProblem && arming && (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">{weekProblem}</p>
      )}
      {!test.seen.armed && alarms.refused && (
        <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty">
          The phone would not arm it: {alarms.refused}
        </p>
      )}
      {alarms.held && (
        <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty">
          A class recording is running, so the alarms are left exactly as they are until it stops.
        </p>
      )}

      <ol className="flex flex-col gap-[9px]" aria-label="What this phone has seen">
        {rows.map((row) => (
          <li key={row.key} className="flex items-start gap-[9px]">
            <span
              aria-hidden
              className={clsx(
                'mt-[1px] flex size-[16px] flex-none items-center justify-center rounded-full',
                row.seen && !row.problem && 'bg-success-fill text-success',
                row.problem && 'border border-warning-line bg-warning-fill text-warning',
                !row.seen && !row.problem && 'border border-line-2',
              )}
            >
              {row.seen && <MCheck size={11} strokeWidth={2.6} />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold leading-[1.3] text-ink">{row.label}</p>
              <p
                className={clsx(
                  'mt-[3px] break-words text-[11.5px] leading-[1.4]',
                  row.problem ? 'text-warning' : row.seen ? 'text-ink-3' : 'text-microcopy',
                )}
              >
                {row.detail}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <p className="text-[11px] leading-[1.45] text-microcopy text-pretty">
        Once uploaded it is in History as an unfiled recording called “{RECORDER_TEST_TITLE}” —
        ignore it or file it. The transcript needs the PC transcriber running.
      </p>

      <button type="button" onClick={state.clear} className={SECONDARY_BUTTON}>
        {finished ? 'Dismiss' : 'Cancel test'}
      </button>
    </Card>
  )
}

/** The confirm sheet: what will happen, in plain words, before anything is armed. */
function RecorderTestSheet({
  blocked,
  phoneUploads,
  onArm,
  onClose,
}: {
  blocked: string | null
  phoneUploads: boolean
  onArm: (plan: RecorderTestPlan) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  useDismissable(ref, onClose)
  const entered = useEntered()

  // The times printed are the times armed. While the sheet sits open they are re-planned before
  // the lead runs short, so "about two minutes" stays true.
  const [plan, setPlan] = useState(() => planRecorderTest(Date.now()))
  useEffect(() => {
    const timer = window.setInterval(() => {
      setPlan((current) =>
        Date.now() > current.promptAtMs - TEST_LEAD_FLOOR_MS ? planRecorderTest(Date.now()) : current,
      )
    }, TEST_CLOCK_MS)
    return () => window.clearInterval(timer)
  }, [])

  const arm = () => {
    onArm(Date.now() > plan.promptAtMs - TEST_LEAD_FLOOR_MS ? planRecorderTest(Date.now()) : plan)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Test the recorder"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-scrim backdrop-blur-[1.5px] motion-safe:animate-[fade-in_var(--dur-scrim)_var(--ease-out)]"
      />
      <div
        ref={ref}
        className={clsx(
          'relative flex max-h-full flex-col gap-[11px] overflow-y-auto rounded-t-pill bg-surface px-[18px] pb-[18px] pt-[14px] shadow-sheet',
          'transition-opacity duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
          entered ? 'opacity-100' : 'opacity-0',
        )}
        style={{ paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 24px))' }}
      >
        <span className="mx-auto h-1 w-[38px] flex-none rounded-[2px] bg-line-2" aria-hidden />
        <h2 className="text-[19px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink">
          Test the recorder
        </h2>
        <p className="text-[13px] leading-[1.5] text-ink-2 text-pretty">
          At {wallTime(plan.promptAtMs)} — about two minutes from now — this phone will prompt you.
          One tap on Start recording starts it. It stops by itself two minutes later, at{' '}
          {wallTime(plan.stopAtMs)}, and then uploads like a real lecture.
        </p>
        <p className="text-[13px] font-semibold leading-[1.4] text-ink">
          Lock the phone now, watch, and tap Start when it asks.
        </p>
        <p className="text-[11.5px] leading-[1.5] text-microcopy text-pretty">
          It will appear as an unfiled recording called “{RECORDER_TEST_TITLE}” — ignore it or file
          it. Your real class alarms stay armed alongside it.{' '}
          {phoneUploads
            ? 'The phone uploads it on its own after it stops — leave LifeOS closed if you like.'
            : 'The upload only runs while LifeOS is open, so come back to this screen after it stops.'}
        </p>
        {blocked && (
          <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[11.5px] leading-[1.45] text-warning text-pretty">
            {blocked}
          </p>
        )}
        <button
          type="button"
          disabled={blocked !== null}
          onClick={arm}
          className="mo-press mt-1 flex min-h-[48px] flex-none items-center justify-center rounded-button bg-ink px-5 text-[14px] font-semibold leading-none text-surface disabled:opacity-60"
        >
          Arm the test
        </button>
        <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
          Cancel
        </button>
      </div>
    </div>
  )
}
